/**
 * The window that shows a tax document's file on the page, opened from a document's name in the
 * Where it came from column, in ValuePicker's source lines, under a new row's name and in the
 * documents table (openSourceDocument in TaxProfile.tsx).
 *
 * Expects the OneDrive item id of the file (tax_document_index.onedrive_item_id, the copy under
 * General/Client Profile/<entity>/Files), the client's Teams team id (the documents response's
 * teamId, which is clients.ms_team_id), the document's title, the callback that opens the file in a
 * new tab, and the callback that closes the window.
 *
 * It asks for the preview address the way the file browser's FilePreviewModal does, with
 * fetchPreviewUrl, which reads GET file/preview/:itemId/:teamId (api/routers/fileRouter.ts). That
 * route asks Microsoft Graph for groups/<teamId>/drive/items/<itemId>/preview, the same team drive
 * and item the api's documents/:documentId/link route reads the file's web address from.
 *
 * Renders the title with an "Open in a new tab" button beside it, and under them the preview in an
 * iframe; a spinner while the address is loading; and a sentence saying the preview could not be
 * loaded, with the same button still there, when the route answers with an error.
 */

import { fetchPreviewUrl } from '@/utils';
import { GenericModal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import {
  DOCUMENT_PREVIEW_WORDS,
  documentPreviewLabel,
} from '@/pages/TaxProfile/wording';

export interface DocumentPreviewModalProps {
  itemId: string;
  teamId: string;
  title: string;
  onOpenInNewTab: () => void;
  onClose: () => void;
}

export default function DocumentPreviewModal({
  itemId,
  teamId,
  title,
  onOpenInNewTab,
  onClose,
}: DocumentPreviewModalProps) {
  const { data: preview, error } = fetchPreviewUrl(itemId, teamId);

  return (
    <GenericModal isOpen onClose={onClose} isFullWidth>
      <div
        role='dialog'
        aria-label={documentPreviewLabel(title)}
        className='flex flex-col gap-3'
      >
        <div className='flex flex-wrap items-center gap-3 pr-8'>
          <h3 className='font-bold text-lg break-words'>{title}</h3>
          <button
            type='button'
            className='btn btn-sm btn-accent'
            onClick={onOpenInNewTab}
          >
            {DOCUMENT_PREVIEW_WORDS.openInNewTab}
          </button>
        </div>
        {error && (
          <p className='text-error text-sm'>{DOCUMENT_PREVIEW_WORDS.failed}</p>
        )}
        {!error && !preview?.getUrl && (
          <div className='flex items-center gap-2 p-2 text-sm'>
            <Spinner />
            {DOCUMENT_PREVIEW_WORDS.loading}
          </div>
        )}
        {!error && preview?.getUrl && (
          // The same address FilePreviewModal builds: the preview url with Office's embedded view.
          <iframe
            src={`${preview.getUrl}&action=embedview&nb=true`}
            title={title}
            className='w-full h-[80vh] bg-transparent border-none'
          />
        )}
      </div>
    </GenericModal>
  );
}
